import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import axios from 'axios'
import Swal from 'sweetalert2'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash, faSpinner, faPaperPlane } from '@fortawesome/free-solid-svg-icons'
import './zMail.scss'
import moment from 'moment'
import { zynity_api } from '../../../../../../config/external-routes'
import { canDeleteMessageForEveryone, canDeleteMessageForMe } from '../../../../../../helpers/zMail'

export const ZMail = ({ conversation, org_id, team_id, isExpanded = false, onToggle = () => {} }) => {
  const PAGE_SIZE = 20
  const user = useSelector((state) => state.auth)
  const [messages, setMessages] = useState(conversation?.messages || [])
  const [messageInput, setMessageInput] = useState('')
  const [sending, setSending] = useState(false)
  const [deletingMessageId, setDeletingMessageId] = useState(null)
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false)
  const [hasMoreMessages, setHasMoreMessages] = useState(Boolean(conversation?.has_more_messages))
  const [nextMessagesCursor, setNextMessagesCursor] = useState(conversation?.next_messages_cursor ?? null)
  const [activeConversationId, setActiveConversationId] = useState(conversation?.id ?? null)
  const messageContainerRef = useRef(null)
  const isPrependingOlderMessagesRef = useRef(false)
  const initialBottomScrollDoneRef = useRef(false)

  const scrollToBottom = () => {
    const container = messageContainerRef.current
    if (!container) return false
    if (container.clientHeight <= 0 || container.scrollHeight <= 0) return false
    container.scrollTop = container.scrollHeight
    return true
  }

  const scheduleInitialBottomScroll = (attempt = 0) => {
    if (initialBottomScrollDoneRef.current) return

    requestAnimationFrame(() => {
      const ok = scrollToBottom()
      if (ok) {
        initialBottomScrollDoneRef.current = true
        return
      }

      if (attempt < 10) {
        setTimeout(() => scheduleInitialBottomScroll(attempt + 1), 70)
      }
    })
  }

  useEffect(() => {
    setMessages(conversation?.messages || [])
    setHasMoreMessages(Boolean(conversation?.has_more_messages))
    setNextMessagesCursor(conversation?.next_messages_cursor ?? null)
    setActiveConversationId(conversation?.id ?? null)
    initialBottomScrollDoneRef.current = false
  }, [conversation?.id, conversation?.messages])

  useEffect(() => {
    if (!isExpanded) return
    initialBottomScrollDoneRef.current = false
    if (sortedMessages.length > 0) {
      scheduleInitialBottomScroll(0)
    }
  }, [isExpanded])

  const participantName = conversation?.participant_name || 'Participant'
  const participantInitials = conversation?.participant_initials || '??'
  const participantId = conversation?.participant_id ?? null

  const sortedMessages = useMemo(() => {
    return [...messages].sort((a, b) => {
      const aTs = a?.created_at ? new Date(a.created_at).getTime() : 0
      const bTs = b?.created_at ? new Date(b.created_at).getTime() : 0
      return aTs - bTs
    })
  }, [messages])

  useEffect(() => {
    if (initialBottomScrollDoneRef.current) return
    if (!isExpanded) return
    if (loadingOlderMessages) return
    if (sortedMessages.length === 0) return

    scheduleInitialBottomScroll(0)
  }, [sortedMessages.length, loadingOlderMessages, isExpanded])

  useEffect(() => {
    const container = messageContainerRef.current
    if (!container) return
    const collapseContainer = container.closest('.accordion-collapse')
    if (!collapseContainer) return

    const onShown = () => {
      if (!isExpanded) return
      if (!initialBottomScrollDoneRef.current && sortedMessages.length > 0) {
        scheduleInitialBottomScroll(0)
      }
    }

    collapseContainer.addEventListener('shown.bs.collapse', onShown)
    return () => {
      collapseContainer.removeEventListener('shown.bs.collapse', onShown)
    }
  }, [sortedMessages.length, isExpanded])

  const normalizeApiMessages = (rawMessages) => {
    return (Array.isArray(rawMessages) ? rawMessages : [])
      .map((item) => ({
        id: item?.id ?? item?.message_id,
        conversation_id: item?.conversation_id ?? activeConversationId,
        sender_id: item?.sender_id ?? item?.from_user_id ?? item?.from_id ?? null,
        sender_name: item?.sender_name ?? item?.from_name ?? item?.user_name ?? 'Unknown',
        sender_initials: item?.sender_initials ?? item?.from_initials ?? null,
        body: item?.body ?? item?.message ?? item?.text ?? '',
        created_at: item?.created_at ?? item?.dateCreated ?? null,
        can_delete: item?.can_delete,
        can_delete_for_me: item?.can_delete_for_me,
        can_delete_for_everyone: item?.can_delete_for_everyone,
      }))
      .filter((item) => item.id != null && item.body)
  }

  const loadOlderMessages = () => {
    if (!activeConversationId || loadingOlderMessages || (!hasMoreMessages && sortedMessages.length > 0)) return

    const oldestMessage = sortedMessages.length > 0 ? sortedMessages[0] : null
    const url = zynity_api.user_organization_team_z_mail_conversation_messages
      .replace('{user_id}', user.id)
      .replace('{organization_id}', org_id)
      .replace('{team_id}', team_id)
      .replace('{conversation_id}', activeConversationId)

    const container = messageContainerRef.current
    const previousScrollHeight = container?.scrollHeight ?? 0
    const previousScrollTop = container?.scrollTop ?? 0

    const params = {
      limit: PAGE_SIZE,
      ...(nextMessagesCursor ? { cursor: nextMessagesCursor } : {}),
      ...(!nextMessagesCursor && oldestMessage?.id ? { before_message_id: oldestMessage.id } : {}),
      ...(!nextMessagesCursor && !oldestMessage?.id && oldestMessage?.created_at ? { before_created_at: oldestMessage.created_at } : {}),
    }

    setLoadingOlderMessages(true)
    axios.defaults.headers.common = { Authorization: `Bearer ${user.access_token}` }

    axios
      .get(url, { params })
      .then((response) => {
        const root = response?.data ?? {}
        const payload = root?.data ?? root
        const rawMessages = Array.isArray(payload) ? payload : payload?.messages ?? payload?.items ?? []
        const normalized = normalizeApiMessages(rawMessages)
        const meta = root?.meta ?? payload?.meta ?? {}
        const apiHasMore = meta?.has_more
        const apiNextCursor = meta?.next_cursor ?? null

        isPrependingOlderMessagesRef.current = true
        setMessages((prev) => {
          const ids = new Set(prev.map((item) => item.id))
          const older = normalized.filter((item) => !ids.has(item.id))
          return [...older, ...prev]
        })

        setHasMoreMessages(typeof apiHasMore === 'boolean' ? apiHasMore : normalized.length >= PAGE_SIZE)
        setNextMessagesCursor(apiNextCursor)

        // Preserve viewport after prepending older messages.
        requestAnimationFrame(() => {
          if (!container) return
          const newScrollHeight = container.scrollHeight
          container.scrollTop = newScrollHeight - previousScrollHeight + previousScrollTop
          isPrependingOlderMessagesRef.current = false
        })
      })
      .catch(() => {
        setHasMoreMessages(false)
        isPrependingOlderMessagesRef.current = false
      })
      .finally(() => {
        setLoadingOlderMessages(false)
      })
  }

  const sendMessage = () => {
    const text = messageInput.trim()
    if (!text) return

    if (participantId == null) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing participant',
        text: 'This chat cannot send messages until participant mapping is available.',
      })
      return
    }

    const payload = {
      assigned_to: participantId,
      message: text,
      conversation_id: conversation?.id,
    }

    const postUrl = zynity_api.user_organization_team_z_mail_post
      .replace('{user_id}', user.id)
      .replace('{organization_id}', org_id)
      .replace('{team_id}', team_id)

    setSending(true)
    axios.defaults.headers.common = { Authorization: `Bearer ${user.access_token}` }

    axios
      .post(postUrl, payload)
      .then((response) => {
        const responseMessage = response?.data?.message || response?.data?.data || null

        if (responseMessage) {
          setMessages((prev) => [
            ...prev,
            {
              id: responseMessage?.id ?? `tmp-${Date.now()}`,
              sender_id: responseMessage?.sender_id ?? user.id,
              sender_name: responseMessage?.sender_name ?? user?.know_as ?? user?.first_name ?? 'You',
              sender_initials: responseMessage?.sender_initials ?? 'ME',
              body: responseMessage?.body ?? responseMessage?.message ?? text,
              created_at: responseMessage?.created_at ?? new Date().toISOString(),
              can_delete: true,
              can_delete_for_me: true,
              can_delete_for_everyone: true,
            },
          ])
        } else {
          setMessages((prev) => [
            ...prev,
            {
              id: `tmp-${Date.now()}`,
              sender_id: user.id,
              sender_name: user?.know_as ?? user?.first_name ?? 'You',
              sender_initials: 'ME',
              body: text,
              created_at: new Date().toISOString(),
              can_delete: true,
              can_delete_for_me: true,
              can_delete_for_everyone: true,
            },
          ])
        }

        setMessageInput('')
        setTimeout(() => {
          scrollToBottom()
        }, 0)
      })
      .catch(() => {
        Swal.fire({
          icon: 'error',
          title: 'Unable to send',
          text: 'Please try again.',
        })
      })
      .finally(() => {
        setSending(false)
      })
  }

  const deleteMessage = (message) => {
    const allowDeleteForMe = canDeleteMessageForMe(message, user?.id)
    const allowDeleteForEveryone = canDeleteMessageForEveryone(message, user?.id)

    if (!allowDeleteForMe && !allowDeleteForEveryone) {
      return
    }

    Swal.fire({
      icon: 'warning',
      title: 'Delete message?',
      text: 'Choose how you want to delete this message.',
      showConfirmButton: allowDeleteForEveryone,
      showDenyButton: allowDeleteForMe,
      showCancelButton: true,
      confirmButtonText: 'For everyone',
      denyButtonText: 'For me only',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
    }).then((result) => {
      const deleteScope = result.isConfirmed ? 'everyone' : result.isDenied ? 'me' : null
      if (!deleteScope) return
      if (deleteScope === 'everyone' && !allowDeleteForEveryone) return
      if (deleteScope === 'me' && !allowDeleteForMe) return

      const newDeleteUrl = zynity_api.user_organization_team_z_mail_message
        .replace('{user_id}', user.id)
        .replace('{organization_id}', org_id)
        .replace('{team_id}', team_id)
        .replace('{message_id}', message.id)

      const legacyDeleteUrl = zynity_api.user_organization_team_z_mail
        .replace('{user_id}', user.id)
        .replace('{organization_id}', org_id)
        .replace('{team_id}', team_id)
        .replace('{z_mail_id}', message.id)

      setDeletingMessageId(message.id)
      axios.defaults.headers.common = { Authorization: `Bearer ${user.access_token}` }

      axios
        .delete(newDeleteUrl, { data: { delete_scope: deleteScope } })
        .catch(() => {
          if (deleteScope === 'everyone') {
            return axios.delete(legacyDeleteUrl)
          }
          return Promise.reject(new Error('DELETE_FOR_ME_NOT_SUPPORTED'))
        })
        .then(() => {
          setMessages((prev) => prev.filter((item) => item.id !== message.id))
          setTimeout(() => {
            scrollToBottom()
          }, 0)
        })
        .catch((error) => {
          if (deleteScope === 'me' && error?.message === 'DELETE_FOR_ME_NOT_SUPPORTED') {
            Swal.fire({
              icon: 'info',
              title: 'Delete for me unavailable',
              text: 'This option requires backend support.',
            })
            return
          }

          Swal.fire({
            icon: 'error',
            title: 'Delete failed',
            text: 'Please try again.',
          })
        })
        .finally(() => {
          setDeletingMessageId(null)
        })
    })
  }

  const onMessageScroll = (event) => {
    if (!isExpanded) return
    if (!initialBottomScrollDoneRef.current) return
    const target = event?.currentTarget
    if (!target) return

    if (target.scrollTop <= 24 && hasMoreMessages && !loadingOlderMessages) {
      loadOlderMessages()
    }
  }

  return (
    <div className="col-12 mb-2 mt-2">
      <div className="zmail-chat-card">
        <button type="button" className="zmail-chat-header zmail-chat-header-btn" onClick={onToggle}>
          <div className="zmail-chat-user">
            <div className="rounded-circle alias_circle">{participantInitials}</div>
            <div>
              <div className="zmail-chat-name">{participantName}</div>
              <div className="zmail-chat-subtitle">Z-private (1:1) conversation</div>
            </div>
          </div>
          <span className={`zmail-chat-chevron ${isExpanded ? 'is-open' : ''}`}>▾</span>
        </button>

        {isExpanded && (
          <>
            <div className="zmail-chat-messages" ref={messageContainerRef} onScroll={onMessageScroll}>
              {loadingOlderMessages && (
                <div className="zmail-loading-older">
                  <FontAwesomeIcon className="spinner" icon={faSpinner} /> Loading older messages...
                </div>
              )}

              {sortedMessages.length === 0 && <div className="zmail-empty">No messages yet.</div>}

              {sortedMessages.map((message) => {
                const ownMessage = Number(message?.sender_id) === Number(user?.id)
                const canDeleteForMe = canDeleteMessageForMe(message, user?.id)
                const canDeleteForEveryone = canDeleteMessageForEveryone(message, user?.id)
                const canDelete = canDeleteForMe || canDeleteForEveryone

                return (
                  <div className={`zmail-bubble-row ${ownMessage ? 'own' : 'other'}`} key={message.id}>
                    <div className="zmail-bubble">
                      <div className="zmail-bubble-meta">
                        <span className="zmail-sender">{ownMessage ? 'You' : message.sender_name}</span>
                        <span className="zmail-date">{message?.created_at ? moment(message.created_at).format('MMM D, YYYY h:mm A') : ''}</span>
                      </div>
                      <div className="zmail-text">{message.body}</div>
                    </div>

                    {canDelete && (
                      <button
                        className="z-mail-trash-icon"
                        disabled={deletingMessageId === message.id}
                        onClick={() => deleteMessage(message)}
                        title="Delete message"
                      >
                        {deletingMessageId === message.id ? (
                          <FontAwesomeIcon className="spinner" icon={faSpinner} />
                        ) : (
                          <FontAwesomeIcon icon={faTrash} />
                        )}
                      </button>
                    )}
                  </div>
                )
              })}
            </div>

            <div className="zmail-reply-row">
              <textarea
                className="zmail-reply-input"
                rows={2}
                placeholder={`Reply to ${participantName}...`}
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
              />
              <button className="zmail-reply-send" disabled={sending || !messageInput.trim()} onClick={sendMessage}>
                {sending ? <FontAwesomeIcon className="spinner" icon={faSpinner} /> : <FontAwesomeIcon icon={faPaperPlane} />}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
